import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { AlertTriangle, Calendar, Package, Building2, Warehouse, Filter, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { productImageSrc, retryProductImageOnce } from '../utils/productImage';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';

export default function ExpirationPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    
    // Data
    const [expiringProducts, setExpiringProducts] = useState([]);
    const [expiredProducts, setExpiredProducts] = useState([]);
    const [summary, setSummary] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [bodegas, setBodegas] = useState([]);
    
    // Filters
    const [filters, setFilters] = useState({
        days: 30,
        sucursal_id: '',
        bodega_id: '',
        view: 'expiring' // 'expiring' | 'expired'
    });

    useEffect(() => {
        fetchSucursales();
        fetchSummary();
    }, []);

    useEffect(() => {
        if (filters.sucursal_id) {
            fetchBodegas(filters.sucursal_id);
        } else {
            setBodegas([]);
        }
    }, [filters.sucursal_id]);

    useEffect(() => {
        if (filters.view === 'expiring') {
            fetchExpiringProducts();
        } else {
            fetchExpiredProducts();
        }
    }, [filters]);

    const fetchSucursales = async () => {
        try {
            const res = await fetch(`${API_URL}/api/sucursales`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSucursales(data.filter(s => s.estado !== false));
            }
        } catch (err) {
            console.error('Error fetching sucursales:', err);
        }
    };

    const fetchBodegas = async (sucursalId) => {
        try {
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setBodegas(await res.json());
            }
        } catch (err) {
            console.error('Error fetching bodegas:', err);
        }
    };

    const fetchExpiringProducts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('days', filters.days);
            if (filters.sucursal_id) params.append('sucursal_id', filters.sucursal_id);
            if (filters.bodega_id) params.append('bodega_id', filters.bodega_id);

            const res = await fetch(`${API_URL}/api/expiration/expiring-soon?${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setExpiringProducts(await res.json());
            }
        } catch (err) {
            console.error('Error fetching expiring products:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchExpiredProducts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.sucursal_id) params.append('sucursal_id', filters.sucursal_id);
            if (filters.bodega_id) params.append('bodega_id', filters.bodega_id);

            const res = await fetch(`${API_URL}/api/expiration/expired?${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setExpiredProducts(await res.json());
            }
        } catch (err) {
            console.error('Error fetching expired products:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const res = await fetch(`${API_URL}/api/expiration/summary`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setSummary(await res.json());
            }
        } catch (err) {
            console.error('Error fetching summary:', err);
        }
    };

    const getStatusColor = (estado) => {
        switch (estado) {
            case 'critico':
                return 'bg-red-100 text-red-700 border-red-300';
            case 'advertencia':
                return 'bg-orange-100 text-orange-700 border-orange-300';
            default:
                return 'bg-yellow-100 text-yellow-700 border-yellow-300';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-CL');
    };

    const products = filters.view === 'expiring' ? expiringProducts : expiredProducts;
    const { sorted: sortedProducts, thProps } = useTableSort(products, {
        defaultKey: 'dias',
        defaultDir: 'asc',
        getters: {
            cantidad_disponible: (item) => Number(item.cantidad_disponible) || 0,
            dias: (item) => Number(filters.view === 'expiring' ? item.dias_restantes : item.dias_vencido) || 0,
        },
    });

    return (
        <div className="page expiration-page">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <AlertTriangle size={28} className="text-orange-500" /> Control de Vencimientos
                    </h1>
                    <p className="text-gray-500">Gestione productos próximos a vencer o vencidos</p>
                </div>
                <button
                    onClick={() => {
                        if (filters.view === 'expiring') {
                            fetchExpiringProducts();
                        } else {
                            fetchExpiredProducts();
                        }
                        fetchSummary();
                    }}
                    className="btn-secondary flex items-center gap-2"
                >
                    <RefreshCw size={18} /> Actualizar
                </button>
            </div>

            {/* Summary Cards */}
            {summary.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {summary.map((sum) => (
                        <div key={`${sum.sucursal_id}_${sum.bodega_id}`} className="bg-white p-4 rounded-lg shadow border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Building2 size={16} className="text-indigo-500" />
                                    <span className="font-semibold text-sm">{sum.sucursal_nombre}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <Warehouse size={14} className="text-gray-400" />
                                <span className="text-xs text-gray-500">{sum.bodega_nombre}</span>
                            </div>
                            <div className="space-y-2">
                                {sum.productos_vencidos > 0 && (
                                    <div className="bg-red-50 p-2 rounded border border-red-200">
                                        <div className="text-xs text-red-600 font-bold">Vencidos: {sum.productos_vencidos}</div>
                                        <div className="text-xs text-red-500">Cantidad: {sum.cantidad_vencida || 0}</div>
                                    </div>
                                )}
                                {sum.productos_vencen_7_dias > 0 && (
                                    <div className="bg-orange-50 p-2 rounded border border-orange-200">
                                        <div className="text-xs text-orange-600 font-bold">Vencen en 7 días: {sum.productos_vencen_7_dias}</div>
                                        <div className="text-xs text-orange-500">Cantidad: {sum.cantidad_vencen_7_dias || 0}</div>
                                    </div>
                                )}
                                {sum.productos_vencen_30_dias > 0 && (
                                    <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                        <div className="text-xs text-yellow-600 font-bold">Vencen en 30 días: {sum.productos_vencen_30_dias}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Filter size={20} className="text-gray-500" />
                    <h3 className="font-semibold">Filtros</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Vista</label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setFilters({ ...filters, view: 'expiring' })}
                                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                                    filters.view === 'expiring'
                                        ? 'bg-orange-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Próximos a Vencer
                            </button>
                            <button
                                onClick={() => setFilters({ ...filters, view: 'expired' })}
                                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                                    filters.view === 'expired'
                                        ? 'bg-red-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Vencidos
                            </button>
                        </div>
                    </div>
                    {filters.view === 'expiring' && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Días a consultar</label>
                            <input
                                type="number"
                                min="1"
                                max="365"
                                className="w-full p-2 border rounded"
                                value={filters.days}
                                onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) || 30 })}
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium mb-1">Sucursal</label>
                        <select
                            className="w-full p-2 border rounded"
                            value={filters.sucursal_id}
                            onChange={(e) => {
                                setFilters({ ...filters, sucursal_id: e.target.value, bodega_id: '' });
                            }}
                        >
                            <option value="">Todas</option>
                            {sucursales.map(s => (
                                <option key={s.id || s.sucursal_id} value={s.id || s.sucursal_id}>
                                    {s.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Bodega</label>
                        <select
                            className="w-full p-2 border rounded"
                            value={filters.bodega_id}
                            onChange={(e) => setFilters({ ...filters, bodega_id: e.target.value })}
                            disabled={!filters.sucursal_id}
                        >
                            <option value="">Todas</option>
                            {bodegas.map(b => (
                                <option key={b.id || b.bodega_id} value={b.id || b.bodega_id}>
                                    {b.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Products Table */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Cargando productos...</div>
                ) : products.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <Package size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>No se encontraron productos {filters.view === 'expiring' ? 'próximos a vencer' : 'vencidos'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <SortableTh columnKey="producto_nombre" {...thProps} className="p-3 text-left text-sm font-semibold">Producto</SortableTh>
                                    <SortableTh columnKey="sucursal_nombre" {...thProps} className="p-3 text-left text-sm font-semibold">Sucursal</SortableTh>
                                    <SortableTh columnKey="bodega_nombre" {...thProps} className="p-3 text-left text-sm font-semibold">Bodega</SortableTh>
                                    <SortableTh columnKey="cantidad_disponible" {...thProps} className="p-3 text-center text-sm font-semibold" align="center">Cantidad</SortableTh>
                                    <SortableTh columnKey="fecha_expiracion" {...thProps} className="p-3 text-center text-sm font-semibold" align="center">Fecha Expiración</SortableTh>
                                    <SortableTh
                                        columnKey="dias"
                                        {...thProps}
                                        className="p-3 text-center text-sm font-semibold"
                                        align="center"
                                    >
                                        {filters.view === 'expiring' ? 'Días Restantes' : 'Días Vencido'}
                                    </SortableTh>
                                    <SortableTh columnKey="estado_vencimiento" {...thProps} className="p-3 text-center text-sm font-semibold" align="center">Estado</SortableTh>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedProducts.map((item) => {
                                    const dias = filters.view === 'expiring' 
                                        ? item.dias_restantes 
                                        : item.dias_vencido;
                                    const estado = item.estado_vencimiento || (filters.view === 'expired' ? 'vencido' : 'normal');
                                    const photo = productImageSrc(item.foto_url);
                                    
                                    return (
                                        <tr key={item.lote_id} className="hover:bg-gray-50">
                                            <td className="p-3">
                                                <div className="dash-product">
                                                    {photo
                                                        ? <img src={photo} alt="" onError={retryProductImageOnce} />
                                                        : <span />}
                                                    <div>
                                                        <div className="font-medium">{item.producto_nombre}</div>
                                                        <div className="text-xs text-gray-500">{item.codigo_barra}</div>
                                                        {item.lote_numero && (
                                                            <div className="text-xs text-gray-400">Lote: {item.lote_numero}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={14} className="text-gray-400" />
                                                    <span>{item.sucursal_nombre || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Warehouse size={14} className="text-gray-400" />
                                                    <span>{item.bodega_nombre || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-center font-bold font-mono">
                                                {item.cantidad_disponible} / {item.cantidad}
                                            </td>
                                            <td className="p-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <Calendar size={14} className="text-gray-400" />
                                                    <span className="font-medium">{formatDate(item.fecha_expiracion)}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className={`font-bold font-mono ${
                                                    filters.view === 'expired' 
                                                        ? 'text-red-600' 
                                                        : dias <= 7 
                                                            ? 'text-red-600' 
                                                            : dias <= 15 
                                                                ? 'text-orange-600' 
                                                                : 'text-gray-600'
                                                }`}>
                                                    {dias} {filters.view === 'expiring' ? 'días' : 'días'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(estado)}`}>
                                                    {estado === 'critico' ? 'Crítico' : estado === 'advertencia' ? 'Advertencia' : estado === 'vencido' ? 'Vencido' : 'Normal'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
